import { CommonModule, DatePipe } from '@angular/common';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { ChangeDetectionStrategy, Component, Inject, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, takeUntil, withLatestFrom } from 'rxjs';
import { NgxsFormPluginModule, UpdateFormValue } from '@ngxs/form-plugin';
import { Select, ofActionSuccessful } from '@ngxs/store';

import { DatepickerYearMonthFormat, NgxsActionName, NgxsFormPath, SelectItems } from '@app/shared/constants';
import { AngularMaterialModule } from '@app/shared/modules/angular-material.module';
import * as AppDataAction from '@app/shared/actions/app-data.action';
import { AppDataState } from '@app/shared/stores/app-data.state';
import { SpinnerButtonComponent } from '@shared/components/spinner-button/spinner-button.component';
import { InjectDialogData } from '@shared/components/edit-modal-dialog/edit-modal-dialog.constant';
import { WebApiResponseBase } from '@app/shared/classes/general';
import { EditModalDialogBasedComponent } from '@shared/components/edit-modal-dialog/edit-modal-dialog.based.component';
import { Achievement } from '@app/shared/classes/achievement';
import { OnlyNumberDirective } from '@app/shared/directive/only-number.directive';
import { MomentDateAdapter, MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_LOCALE, MAT_DATE_FORMATS } from '@angular/material/core';
import { MatDatepicker } from '@angular/material/datepicker';
import { DateTimeFormatStrings } from '@app/shared/enums';
import { Utilities } from '@app/shared/utils/utilities';

@Component({
  selector: 'app-achievement-edit-modal-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, SpinnerButtonComponent, TranslateModule, NgxsFormPluginModule, ReactiveFormsModule, MatDialogModule, AngularMaterialModule, OnlyNumberDirective],
  templateUrl: './achievement-edit-modal-dialog.component.html',
  styleUrl: './achievement-edit-modal-dialog.component.css',
  providers: [
    DatePipe,
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    { provide: MAT_DATE_FORMATS, useValue: DatepickerYearMonthFormat },
  ],
})
export class AchievementEditModalDialogComponent extends EditModalDialogBasedComponent<AchievementEditModalDialogComponent, Achievement> {
  dateAdapter = inject(DateAdapter);
  datePipe = inject(DatePipe);

  complianceSelectItems = SelectItems.complianceSelectItems;
  formPath = NgxsFormPath.ACHIEVEMENT_EDIT_FORM;

  @Select(AppDataState.taskExecuting(NgxsActionName.UPDATE_ACHIEVEMENT)) updating$: Observable<boolean>;
  @Select(AppDataState.taskError(NgxsActionName.UPDATE_ACHIEVEMENT)) response$: Observable<WebApiResponseBase>;

  constructor(public dialogRef: MatDialogRef<AchievementEditModalDialogComponent>, @Inject(MAT_DIALOG_DATA) public data: InjectDialogData<Achievement>) {
    super();
    this.dataInit(dialogRef, data);
    this.initForm(data);
  }

  initForm = (data: InjectDialogData<Achievement>) => {
    var achieveDate = Utilities.formatDate(new Date(data.data.achieveDate), DateTimeFormatStrings.FORMAT_6);
    this.editForm = this.formBuilder.group({
      achieve_Id: data.data.achieve_id,
      mileage: [data.data.mileage, [Validators.required, Validators.pattern(/^-?[0-9]\d*$/)]],
      cylinder: [data.data.cylinder, [Validators.required, Validators.pattern(/^-?[0-9]\d*$/)]],
      achieveDate: [achieveDate, Validators.required]
    });
  }

  override ngOnInit() {
    super.ngOnInit();
    this.actions$.pipe(ofActionSuccessful(AppDataAction.UpdateAchievement), withLatestFrom(this.response$), takeUntil(this.destroy$)).subscribe(
      ([_, errorResponse]) => this.processAfterUpdateObject(errorResponse));
  }

  onOkClick = () => {
    this.store.dispatch(new AppDataAction.UpdateAchievement(this.data.data));
  }

  // This is used for month select controls
  setMonthAndYear = (normalizedMonthAndYear: Date, datepicker: MatDatepicker<Date>, formControlName: string) => {
    let selectedDate = this.datePipe.transform(normalizedMonthAndYear, DateTimeFormatStrings.FORMAT_6);
    this.editForm.controls[formControlName].setValue(selectedDate);
    this.store.dispatch(new UpdateFormValue({
      value: selectedDate,
      path: this.formPath,
      propertyPath: formControlName
    }));
    datepicker.close();
  }
}
